import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatAngularModule } from '../../shared/modules/mat-angular.module';
import { AdminService, AuditType, AuditQuestionsInfo, ServiceVerificationResponse, ApiKeyResponse } from '../../services/admin.service';
import { AuthService } from '../../services/auth.service';
import { MatSnackBar } from '@angular/material/snack-bar';

interface AuditCard {
  type: AuditType;
  title: string;
  description: string;
  info: AuditQuestionsInfo | null;
  selectedFile: File | null;
  uploading: boolean;
  downloading: boolean;
}

@Component({
  selector: 'app-settings',
  standalone: true,
  imports: [CommonModule, FormsModule, MatAngularModule],
  templateUrl: './settings.component.html',
  styleUrl: './settings.component.scss'
})
export class SettingsComponent implements OnInit {
  private adminService = inject(AdminService);
  private authService = inject(AuthService);
  private snackBar = inject(MatSnackBar);

  isAdmin = false;
  showSectionReloadButton = false;
  loadingUserSettings = false;
  savingSectionReloadSetting = false;

  citedDomains = {
    selectedFile: null as File | null,
    uploading: false,
    downloading: false,
    override: false,
    strict: false
  };

  serviceVerification = {
    scraper: {
      verifying: false,
      status: null as 'working' | 'failed' | null,
      message: '',
      lastChecked: null as Date | null
    },
    tavily: {
      verifying: false,
      status: null as 'working' | 'failed' | null,
      message: '',
      lastChecked: null as Date | null
    },
    openai: {
      verifying: false,
      status: null as 'working' | 'failed' | null,
      message: '',
      lastChecked: null as Date | null
    },
    gemini: {
      verifying: false,
      status: null as 'working' | 'failed' | null,
      message: '',
      lastChecked: null as Date | null
    },
    oxylabs: {
      verifying: false,
      status: null as 'working' | 'failed' | null,
      message: '',
      lastChecked: null as Date | null
    }
  };

  apiKeys: ApiKeyResponse[] = [];
  editingKey: { serviceName: string; newKey: string } | null = null;
  editingOxylabs: { username: string; password: string } | null = null;
  loadingKeys = false;

  getServiceName(serviceName: string): string {
    const names: { [key: string]: string } = {
      'tavily': 'Tavily API Key',
      'scraperapi': 'ScraperAPI Key',
      'openai': 'ChatGPT API Key',
      'gemini': 'Gemini API Key',
      'oxylabs': 'Web search'
    };
    return names[serviceName] || serviceName;
  }

  getServiceDescription(serviceName: string): string {
    const descriptions: { [key: string]: string } = {
      'tavily': 'Web search service',
      'scraperapi': 'Web scraping service',
      'openai': 'OpenAI ChatGPT service',
      'gemini': 'Google Gemini service',
      'oxylabs': 'Web search service (Google Search API)'
    };
    return descriptions[serviceName] || 'API service';
  }

  getServiceUrl(key: ApiKeyResponse): string {
    return key.management_url || '#';
  }

  hasVerification(serviceName: string): boolean {
    return serviceName === 'tavily' || serviceName === 'scraperapi' || serviceName === 'openai' || serviceName === 'gemini' || serviceName === 'oxylabs';
  }

  verifyOpenAIService(): void {
    this.serviceVerification.openai.verifying = true;
    this.adminService.verifyOpenAIService().subscribe({
      next: (res) => {
        this.serviceVerification.openai.status = res.status;
        this.serviceVerification.openai.message = res.message;
        this.serviceVerification.openai.lastChecked = new Date(res.timestamp);
        this.serviceVerification.openai.verifying = false;
        this.snackBar.open(`OpenAI service: ${res.message}`, 'Close', { duration: 3000 });
        // Reload API keys to get updated verification status and timestamp from database
        this.loadApiKeys();
      },
      error: (err) => {
        this.serviceVerification.openai.status = 'failed';
        this.serviceVerification.openai.message = err.error?.detail || err.error?.message || 'Verification failed';
        this.serviceVerification.openai.lastChecked = new Date();
        this.serviceVerification.openai.verifying = false;
        this.snackBar.open(`OpenAI service: ${this.serviceVerification.openai.message}`, 'Close', { duration: 5000 });
        // Reload API keys to get updated verification status and timestamp from database
        this.loadApiKeys();
      }
    });
  }

  verifyGeminiService(): void {
    this.serviceVerification.gemini.verifying = true;
    this.adminService.verifyGeminiService().subscribe({
      next: (res) => {
        this.serviceVerification.gemini.status = res.status;
        this.serviceVerification.gemini.message = res.message;
        this.serviceVerification.gemini.lastChecked = new Date(res.timestamp);
        this.serviceVerification.gemini.verifying = false;
        this.snackBar.open(`Gemini service: ${res.message}`, 'Close', { duration: 3000 });
        // Reload API keys to get updated verification status and timestamp from database
        this.loadApiKeys();
      },
      error: (err) => {
        this.serviceVerification.gemini.status = 'failed';
        this.serviceVerification.gemini.message = err.error?.detail || err.error?.message || 'Verification failed';
        this.serviceVerification.gemini.lastChecked = new Date();
        this.serviceVerification.gemini.verifying = false;
        this.snackBar.open(`Gemini service: ${this.serviceVerification.gemini.message}`, 'Close', { duration: 5000 });
        // Reload API keys to get updated verification status and timestamp from database
        this.loadApiKeys();
      }
    });
  }

  verifyOxylabsService(): void {
    this.serviceVerification.oxylabs.verifying = true;
    this.adminService.verifyOxylabsService().subscribe({
      next: (res) => {
        this.serviceVerification.oxylabs.status = res.status;
        this.serviceVerification.oxylabs.message = res.message;
        this.serviceVerification.oxylabs.lastChecked = new Date(res.timestamp);
        this.serviceVerification.oxylabs.verifying = false;
        this.snackBar.open(`Web search: ${res.message}`, 'Close', { duration: 3000 });
        // Reload API keys to get updated verification status and timestamp from database
        this.loadApiKeys();
      },
      error: (err) => {
        this.serviceVerification.oxylabs.status = 'failed';
        this.serviceVerification.oxylabs.message = err.error?.detail || err.error?.message || 'Verification failed';
        this.serviceVerification.oxylabs.lastChecked = new Date();
        this.serviceVerification.oxylabs.verifying = false;
        this.snackBar.open(`Web search: ${this.serviceVerification.oxylabs.message}`, 'Close', { duration: 5000 });
        // Reload API keys to get updated verification status and timestamp from database
        this.loadApiKeys();
      }
    });
  }

  // Helper methods for verification UI
  getVerificationStatus(serviceName: string): 'working' | 'failed' | null {
    if (serviceName === 'scraperapi') return this.serviceVerification.scraper.status;
    if (serviceName === 'tavily') return this.serviceVerification.tavily.status;
    if (serviceName === 'openai') return this.serviceVerification.openai.status;
    if (serviceName === 'gemini') return this.serviceVerification.gemini.status;
    if (serviceName === 'oxylabs') return this.serviceVerification.oxylabs.status;
    return null;
  }

  getVerificationMessage(serviceName: string): string {
    if (serviceName === 'scraperapi') return this.serviceVerification.scraper.message;
    if (serviceName === 'tavily') return this.serviceVerification.tavily.message;
    if (serviceName === 'openai') return this.serviceVerification.openai.message;
    if (serviceName === 'gemini') return this.serviceVerification.gemini.message;
    if (serviceName === 'oxylabs') return this.serviceVerification.oxylabs.message;
    return '';
  }

  getVerificationLastChecked(serviceName: string): Date | null {
    if (serviceName === 'scraperapi') return this.serviceVerification.scraper.lastChecked;
    if (serviceName === 'tavily') return this.serviceVerification.tavily.lastChecked;
    if (serviceName === 'openai') return this.serviceVerification.openai.lastChecked;
    if (serviceName === 'gemini') return this.serviceVerification.gemini.lastChecked;
    if (serviceName === 'oxylabs') return this.serviceVerification.oxylabs.lastChecked;
    return null;
  }

  isVerifying(serviceName: string): boolean {
    if (serviceName === 'scraperapi') return this.serviceVerification.scraper.verifying;
    if (serviceName === 'tavily') return this.serviceVerification.tavily.verifying;
    if (serviceName === 'openai') return this.serviceVerification.openai.verifying;
    if (serviceName === 'gemini') return this.serviceVerification.gemini.verifying;
    if (serviceName === 'oxylabs') return this.serviceVerification.oxylabs.verifying;
    return false;
  }

  verifyService(serviceName: string): void {
    if (serviceName === 'scraperapi') {
      this.verifyScraperService();
    } else if (serviceName === 'tavily') {
      this.verifyTavilyService();
    } else if (serviceName === 'openai') {
      this.verifyOpenAIService();
    } else if (serviceName === 'gemini') {
      this.verifyGeminiService();
    } else if (serviceName === 'oxylabs') {
      this.verifyOxylabsService();
    }
  }

  getVerificationDescription(serviceName: string): string {
    const descriptions: { [key: string]: string } = {
      'scraperapi': 'Verify web scraping functionality',
      'tavily': 'Verify web search functionality',
      'oxylabs': 'Verify web search connectivity',
      'openai': 'Verify ChatGPT API functionality',
      'gemini': 'Verify Gemini API functionality'
    };
    return descriptions[serviceName] || 'Verify service functionality';
  }

  auditCards: AuditCard[] = [
    {
      type: 'eeat',
      title: 'Upload EEAT Audit questions',
      description: 'Upload CSV file with EEAT audit questions (Dimension, Question, Pass/Fail)',
      info: null,
      selectedFile: null,
      uploading: false,
      downloading: false
    },
    {
      type: 'ai-audit',
      title: 'Upload AI Audit questions',
      description: 'Upload CSV file with AI audit questions (Category, Question, Options)',
      info: null,
      selectedFile: null,
      uploading: false,
      downloading: false
    },
    {
      type: 'landing-page',
      title: 'Upload Landing Page audit questions',
      description: 'Upload CSV file with Landing Page audit questions (Landing page audit area, Question, Pass/Fail)',
      info: null,
      selectedFile: null,
      uploading: false,
      downloading: false
    }
  ];

  ngOnInit(): void {
    this.isAdmin = this.authService.isAdmin();
    this.loadAllAuditInfo();
    this.loadApiKeys();
    this.loadUserSettings();
  }

  loadApiKeys(): void {
    this.loadingKeys = true;
    this.adminService.getApiKeys().subscribe({
      next: (keys) => {
        this.apiKeys = keys;
        this.loadingKeys = false;
        // Update verification status from API keys
        keys.forEach(key => {
          if (key.service_name === 'scraperapi') {
            if (key.last_verification_status) {
              this.serviceVerification.scraper.status = key.last_verification_status;
              this.serviceVerification.scraper.lastChecked = key.last_verified_at ? new Date(key.last_verified_at) : null;
            }
          } else if (key.service_name === 'tavily') {
            if (key.last_verification_status) {
              this.serviceVerification.tavily.status = key.last_verification_status;
              this.serviceVerification.tavily.lastChecked = key.last_verified_at ? new Date(key.last_verified_at) : null;
            }
          } else if (key.service_name === 'openai') {
            if (key.last_verification_status) {
              this.serviceVerification.openai.status = key.last_verification_status;
              this.serviceVerification.openai.lastChecked = key.last_verified_at ? new Date(key.last_verified_at) : null;
            }
          } else if (key.service_name === 'gemini') {
            if (key.last_verification_status) {
              this.serviceVerification.gemini.status = key.last_verification_status;
              this.serviceVerification.gemini.lastChecked = key.last_verified_at ? new Date(key.last_verified_at) : null;
            }
          } else if (key.service_name === 'oxylabs') {
            if (key.last_verification_status) {
              this.serviceVerification.oxylabs.status = key.last_verification_status;
              this.serviceVerification.oxylabs.lastChecked = key.last_verified_at ? new Date(key.last_verified_at) : null;
            }
          }
        });
      },
      error: (err) => {
        console.error('Failed to load API keys:', err);
        this.loadingKeys = false;
      }
    });
  }

  loadAllAuditInfo(): void {
    this.auditCards.forEach(card => {
      this.adminService.getAuditQuestionsInfo(card.type).subscribe({
        next: (info) => {
          card.info = info;
        },
        error: (err) => {
          console.error(`Failed to load info for ${card.type}:`, err);
        }
      });
    });
  }

  onFileSelected(event: Event, card: AuditCard): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      if (file.name.endsWith('.csv')) {
        card.selectedFile = file;
      } else {
        this.snackBar.open('Please select a CSV file', 'Close', { duration: 3000 });
        input.value = '';
      }
    }
  }

  uploadFile(card: AuditCard): void {
    if (!card.selectedFile) {
      this.snackBar.open('Please select a CSV file first', 'Close', { duration: 3000 });
      return;
    }

    card.uploading = true;

    this.adminService.uploadAuditQuestions(card.type, card.selectedFile).subscribe({
      next: (response) => {
        card.uploading = false;
        card.selectedFile = null;
        this.snackBar.open(
          `Successfully uploaded ${response.row_count} questions for ${card.type}`,
          'Close',
          { duration: 5000 }
        );
        // Reload info
        this.loadAuditInfo(card);
        // Reset file input
        const fileInput = document.getElementById(`file-input-${card.type}`) as HTMLInputElement;
        if (fileInput) {
          fileInput.value = '';
        }
      },
      error: (err) => {
        card.uploading = false;
        card.selectedFile = null;
        const errorMessage = err.error?.detail || err.error?.message || 'Upload failed';
        this.snackBar.open(errorMessage, 'Close', { duration: 5000 });
        // Refresh info so UI reflects actual server state after failure
        this.loadAuditInfo(card);
        // Reset file input to avoid showing stale filename
        const fileInput = document.getElementById(`file-input-${card.type}`) as HTMLInputElement;
        if (fileInput) {
          fileInput.value = '';
        }
      }
    });
  }

  downloadFile(card: AuditCard): void {
    card.downloading = true;

    this.adminService.downloadAuditQuestions(card.type).subscribe({
      next: (blob) => {
        card.downloading = false;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${card.type}-questions.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        this.snackBar.open(`Downloaded ${card.type} questions CSV`, 'Close', { duration: 3000 });
      },
      error: (err) => {
        card.downloading = false;
        const errorMessage = err.error?.detail || err.error?.message || 'Download failed';
        this.snackBar.open(errorMessage, 'Close', { duration: 5000 });
      }
    });
  }

  private loadAuditInfo(card: AuditCard): void {
    this.adminService.getAuditQuestionsInfo(card.type).subscribe({
      next: (info) => {
        card.info = info;
      },
      error: (err) => {
        console.error(`Failed to load info for ${card.type}:`, err);
      }
    });
  }

  getFileTypeLabel(card: AuditCard): string {
    if (!card.info) return 'Unknown';
    return card.info.file_type === 'custom' ? 'Custom' : 'Default';
  }

  // --- User settings (section reload button, admin only) ---
  loadUserSettings(): void {
    if (!this.authService.isAdmin()) return;
    this.loadingUserSettings = true;
    this.authService.getUserSettings().subscribe({
      next: (s) => {
        this.showSectionReloadButton = s.show_section_reload_button ?? false;
        this.loadingUserSettings = false;
      },
      error: () => {
        this.loadingUserSettings = false;
      }
    });
  }

  onShowSectionReloadButtonChange(checked: boolean): void {
    this.savingSectionReloadSetting = true;
    this.authService.updateUserSettings({ show_section_reload_button: checked }).subscribe({
      next: (s) => {
        this.showSectionReloadButton = s.show_section_reload_button ?? false;
        this.savingSectionReloadSetting = false;
        this.snackBar.open('Setting saved', 'Close', { duration: 2000 });
      },
      error: () => {
        this.savingSectionReloadSetting = false;
        this.snackBar.open('Failed to save setting', 'Close', { duration: 3000 });
      }
    });
  }

  // --- Cited domains (settings) ---
  onCitedFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      if (file.name.endsWith('.csv')) {
        this.citedDomains.selectedFile = file;
      } else {
        this.snackBar.open('Please select a CSV file', 'Close', { duration: 3000 });
        input.value = '';
      }
    }
  }

  uploadCitedDomains(): void {
    if (!this.citedDomains.selectedFile) {
      this.snackBar.open('Please select a CSV file first', 'Close', { duration: 3000 });
      return;
    }

    this.citedDomains.uploading = true;

    this.adminService.uploadCitedDomains(
      this.citedDomains.selectedFile,
      this.citedDomains.override,
      this.citedDomains.strict
    ).subscribe({
      next: (res) => {
        this.citedDomains.uploading = false;
        this.citedDomains.selectedFile = null;
        this.snackBar.open(
          `Uploaded ${res.rows_processed} domains${res.override ? ' (override)' : ' (upsert)'}`,
          'Close',
          { duration: 5000 }
        );
        const fileInput = document.getElementById('cited-file-input') as HTMLInputElement;
        if (fileInput) fileInput.value = '';
      },
      error: (err) => {
        this.citedDomains.uploading = false;
        this.citedDomains.selectedFile = null;
        const msg = err?.error?.detail || err?.error?.message || 'Upload failed';
        this.snackBar.open(msg, 'Close', { duration: 5000 });
        const fileInput = document.getElementById('cited-file-input') as HTMLInputElement;
        if (fileInput) fileInput.value = '';
      }
    });
  }

  downloadCitedDomains(): void {
    this.citedDomains.downloading = true;
    this.adminService.downloadCitedDomains().subscribe({
      next: (blob) => {
        this.citedDomains.downloading = false;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'cited-domains.csv';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        this.snackBar.open('Downloaded cited domains CSV', 'Close', { duration: 3000 });
      },
      error: (err) => {
        this.citedDomains.downloading = false;
        const msg = err?.error?.detail || err?.error?.message || 'Download failed';
        this.snackBar.open(msg, 'Close', { duration: 5000 });
      }
    });
  }

  // Service Verification Methods
  verifyScraperService(): void {
    this.serviceVerification.scraper.verifying = true;
    this.adminService.verifyScraperService().subscribe({
      next: (response: ServiceVerificationResponse) => {
        this.serviceVerification.scraper.verifying = false;
        this.serviceVerification.scraper.status = response.status;
        this.serviceVerification.scraper.message = response.message;
        this.serviceVerification.scraper.lastChecked = new Date();
        
        if (response.status === 'working') {
          this.snackBar.open('Scraper service is working', 'Close', { duration: 3000 });
        } else {
          this.snackBar.open('Scraper service verification failed', 'Close', { duration: 5000 });
        }
      },
      error: (err) => {
        this.serviceVerification.scraper.verifying = false;
        this.serviceVerification.scraper.status = 'failed';
        this.serviceVerification.scraper.message = err.error?.detail || err.error?.message || 'Verification failed';
        this.serviceVerification.scraper.lastChecked = new Date();
        this.snackBar.open('Failed to verify scraper service', 'Close', { duration: 5000 });
      }
    });
  }

  verifyTavilyService(): void {
    this.serviceVerification.tavily.verifying = true;
    this.adminService.verifyTavilyService().subscribe({
      next: (res) => {
        this.serviceVerification.tavily.status = res.status;
        this.serviceVerification.tavily.message = res.message;
        this.serviceVerification.tavily.lastChecked = new Date(res.timestamp);
        this.serviceVerification.tavily.verifying = false;
        this.snackBar.open(`Tavily service: ${res.message}`, 'Close', { duration: 3000 });
        // Reload API keys to get updated verification status and timestamp from database
        this.loadApiKeys();
      },
      error: (err) => {
        this.serviceVerification.tavily.status = 'failed';
        this.serviceVerification.tavily.message = err.error?.detail || err.error?.message || 'Verification failed';
        this.serviceVerification.tavily.lastChecked = new Date();
        this.serviceVerification.tavily.verifying = false;
        this.snackBar.open(`Tavily service: ${this.serviceVerification.tavily.message}`, 'Close', { duration: 5000 });
        // Reload API keys to get updated verification status and timestamp from database
        this.loadApiKeys();
      }
    });
  }

  startEditingKey(key: ApiKeyResponse): void {
    // Special handling for web search credentials (username/password)
    if (key.service_name === 'oxylabs') {
      // Try to parse existing credentials from masked key display
      // The masked key format is: "username: ****abc, password: ****xyz"
      // We'll start with empty fields and let user enter new values
      this.editingOxylabs = {
        username: '',
        password: ''
      };
      this.editingKey = null;
    } else {
      this.editingKey = {
        serviceName: key.service_name,
        newKey: ''
      };
      this.editingOxylabs = null;
    }
  }

  cancelEditingKey(): void {
    this.editingKey = null;
    this.editingOxylabs = null;
  }

  updateApiKey(serviceName: string): void {
    // Special handling for web search credentials
    if (serviceName === 'oxylabs') {
      if (!this.editingOxylabs || !this.editingOxylabs.username.trim() || !this.editingOxylabs.password.trim()) {
        this.snackBar.open('Please enter both username and password', 'Close', { duration: 3000 });
        return;
      }

      // Build JSON credentials
      const credentialsJson = JSON.stringify({
        username: this.editingOxylabs.username.trim(),
        password: this.editingOxylabs.password.trim()
      });

      this.adminService.updateApiKey(serviceName, credentialsJson).subscribe({
        next: (res) => {
          this.snackBar.open(res.message, 'Close', { duration: 3000 });
          this.editingKey = null;
          this.editingOxylabs = null;
          this.loadApiKeys();
        },
        error: (err) => {
          const errorMessage = err.error?.detail || err.error?.message || 'Failed to update credentials';
          this.snackBar.open(errorMessage, 'Close', { duration: 5000 });
        }
      });
    } else {
      // Regular API key update
      if (!this.editingKey || !this.editingKey.newKey.trim()) {
        this.snackBar.open('Please enter a valid API key', 'Close', { duration: 3000 });
        return;
      }

      this.adminService.updateApiKey(serviceName, this.editingKey.newKey.trim()).subscribe({
        next: (res) => {
          this.snackBar.open(res.message, 'Close', { duration: 3000 });
          this.editingKey = null;
          this.loadApiKeys();
        },
        error: (err) => {
          const errorMessage = err.error?.detail || err.error?.message || 'Failed to update API key';
          this.snackBar.open(errorMessage, 'Close', { duration: 5000 });
        }
      });
    }
  }

  getApiKey(serviceName: string): ApiKeyResponse | undefined {
    return this.apiKeys.find(k => k.service_name === serviceName);
  }

}
